/**
 * Scroll Tracking
 * Tracks when sections come into view using Intersection Observer
 */

import type { Analytics } from "./index";

/**
 * Set up scroll tracking for page sections
 * Uses Intersection Observer to track when sections enter viewport
 * @param analytics - Analytics instance to send events to
 * @param sections - Array of section IDs to track (e.g., ['hero', 'features'])
 * @param threshold - Percentage of section that must be visible (0.0 to 1.0)
 */
export function trackSectionViews(
  analytics: Analytics,
  sections: string[] = ["hero", "features", "demo", "pricing", "final-cta"],
  threshold = 0.5,
): () => void {
  const tracked = new Set<string>();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const sectionId = entry.target.id;

        // Only track once per section and only when it's intersecting
        if (entry.isIntersecting && !tracked.has(sectionId)) {
          analytics.trackSectionViewed({
            section: sectionId,
            scroll_depth: Math.round(entry.intersectionRatio * 100),
          });
          tracked.add(sectionId);
        }
      });
    },
    {
      threshold,
      // Root margin can be adjusted to trigger slightly before/after entering viewport
      rootMargin: "0px",
    },
  );

  // Observe all specified sections
  sections.forEach((id) => {
    const element = document.getElementById(id);
    if (element) {
      observer.observe(element);
    }
  });

  // Return cleanup function
  return () => {
    observer.disconnect();
  };
}

/**
 * Track scroll depth as percentage of page
 * Useful for understanding overall engagement
 * @param analytics - Analytics instance to send events to
 * @param thresholds - Array of percentage thresholds to track (e.g., [25, 50, 75, 100])
 */
export function trackScrollDepth(
  analytics: Analytics,
  thresholds: number[] = [25, 50, 75, 100],
): () => void {
  const tracked = new Set<number>();

  const handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const clientHeight = window.innerHeight;

    // Calculate scroll percentage
    const scrolled = ((scrollTop + clientHeight) / scrollHeight) * 100;

    // Track each threshold once
    thresholds.forEach((threshold) => {
      if (scrolled >= threshold && !tracked.has(threshold)) {
        analytics.trackSectionViewed({
          section: "page",
          scroll_depth: threshold,
        });
        tracked.add(threshold);
      }
    });
  };

  // Throttle scroll events
  let ticking = false;
  const throttledScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        handleScroll();
        ticking = false;
      });
      ticking = true;
    }
  };

  window.addEventListener("scroll", throttledScroll, { passive: true });

  // Return cleanup function
  return () => {
    window.removeEventListener("scroll", throttledScroll);
  };
}

